import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import API from '@/utils/api';

/**
 * AuthCallback - Handles OAuth redirect
 * 
 * Receives session_id from Emergent OAuth and exchanges it for tokens.
 * This page should never show for more than a moment.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setIsAuthenticated } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Extract session_id from URL hash
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          throw new Error('No session ID received');
        }

        // Exchange session_id for tokens via backend
        const response = await API.post('/auth/v2/emergent/session', {
          session_id: sessionId
        });

        if (response.data?.user) {
          // Update auth context
          setUser(response.data.user);
          setIsAuthenticated(true);

          // Store tokens if returned (for native apps)
          if (response.data.access_token) {
            localStorage.setItem('fmn_access_token', response.data.access_token);
          }

          // Clear the hash from URL
          window.history.replaceState(null, '', window.location.pathname);

          // Navigate to home/dashboard
          navigate('/', { 
            replace: true,
            state: { user: response.data.user }
          });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message || 'Authentication failed');
        
        // Redirect to login after showing error
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    processCallback();
  }, [navigate, setUser, setIsAuthenticated]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F7F5] flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Authentication Failed</h2>
          <p className="text-slate-500 mb-4">{error}</p>
          <p className="text-sm text-slate-400">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7F5] flex flex-col items-center justify-center p-6">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Completing Sign In</h2>
        <p className="text-slate-500">Please wait...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
